import { addSystemLog } from "wasp/client/operations";

declare global {
  interface Window {
    gtag: (
      command: 'event',
      action: string,
      params: GtagEventParams
    ) => void;
    betterdocs: any
  }
}

interface GtagEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: any;
}

/* --------------------------------------------------- */
/*                  Event Types                        */
/* --------------------------------------------------- */

interface LoginEventParams extends GtagEventParams {
  method: string;
  email: string;
}

interface SignupStartedParams extends GtagEventParams {
  referralSource: string;
}

interface SignupCompletedParams extends GtagEventParams {
  method: string;
  email: string;
}

interface LogoutEventParams extends GtagEventParams {
  email: string;
}

interface OnboardingStartedParams extends GtagEventParams {
  email: string;

}

interface OnboardingStepViewedParams extends GtagEventParams {
  step: number;
  stepName: string;
  email: string;

}

interface OnboardingStepCompletedParams extends GtagEventParams {
  step: number;
  stepName: string;
  email: string;

}

interface OnboardingFinishedParams extends GtagEventParams {
  completedSteps: number;
  email: string;

}

interface PricingPageViewedParams extends GtagEventParams {
 source: "onboarding" | "pricing_page" 
 email: string;

}

interface PricingPlanSelectedParams extends GtagEventParams {
  plan: string;
  email: string;

}

interface PlanSelectionSkippedParams extends GtagEventParams {
  email: string;

}

interface DashboardFirstTimeViewedParams extends GtagEventParams {
  email: string;

}

interface AnalysisButtonClickedParams extends GtagEventParams {
  entryPoint: "dashboard" | "products_page";
  email: string;

}

interface ShopifyConnectionStartedParams extends GtagEventParams {
  email: string;

}

interface ShopifyConnectionCompletedParams extends GtagEventParams {
  email: string;

}

interface AnalysisStartedParams extends GtagEventParams {
  email: string;

}

interface AnalysisInProgressParams extends GtagEventParams {
 progress: number; 
 email: string;

}

interface AnalysisCompletedParams extends GtagEventParams {
  email: string;

}

interface EnhancementMenuOpenedParams extends GtagEventParams {
  email: string;

}

interface EnhancementJobStartedParams extends GtagEventParams {
  jobId: string;
  features: string[];  
  email: string;

}

interface EnhancementJobCompletedParams extends GtagEventParams {
  jobId: string;
  email: string;

}

interface EnhancementJobResultsEditedParams extends GtagEventParams {
  jobId: string;
  email: string;

}

interface EnhancementJobExportedParams extends GtagEventParams {
  jobId: string;
  destination: "shopify" | "csv";
  email: string;

}

interface SupportChatOpenedParams extends GtagEventParams {
  email: string;

}

interface PlanUpgredeFlowStartedParams extends GtagEventParams {
  email: string;

}

interface PlanUpgredeFlowCompletedParams extends GtagEventParams {
  email: string;

  
}


/* --------------------------------------------------- */
/*                 End Event Types                        */
/* --------------------------------------------------- */


class AnalyticsManager {
  private static instance: AnalyticsManager;
  private isInitialized: boolean = false;
  private lastEvents: Map<string, number> = new Map();
  private readonly DEBOUNCE_INTERVAL = 100; // 100ms

  private constructor() {
    
  }

  public static getInstance(): AnalyticsManager {
    if (!AnalyticsManager.instance) {
      AnalyticsManager.instance = new AnalyticsManager();
    }
    return AnalyticsManager.instance;
  }

  private shouldSendEvent(action: string): boolean {
    const now = Date.now();
    const lastEventTime = this.lastEvents.get(action);

    if (!lastEventTime) {
      this.lastEvents.set(action, now);
      return true;
    }

    if (now - lastEventTime < this.DEBOUNCE_INTERVAL) {
      return false;
    }

    this.lastEvents.set(action, now);
    return true;
  }

  private sendEvent(action: string, params: GtagEventParams): void {
    if (!this.shouldSendEvent(action)) {
      return;
    }

    if (typeof window !== 'undefined' && window.gtag && this.isInitialized) {
      window.gtag('event', action, params);
    } else {
      console.warn('Google Analytics not initialized or window not available');
    }
  }

  public init(): void {
    if (!this.isInitialized && typeof window !== 'undefined') {
      this.isInitialized = true;
      console.log('Analytics Manager initialized');
      addSystemLog({
        message: 'Analytics system initialized for a user.',
        level: 'info',
      });
    }
  }

  //ADDED
  public sendLoginEvent(params: LoginEventParams): void {
    this.sendEvent('login', {
      event_category: 'authentication',
      ...params
    });
    addSystemLog({
      message: `${params.email} logged in with ${params.method}`,
      level: 'info',
    });
  }

  //ADDED
  public sendSignupStartedEvent(params: SignupStartedParams): void {
    this.sendEvent('signup_started', {
      event_category: 'authentication',
      ...params
    });
    addSystemLog({
      message: `A user started signup. Referral source: ${params.referralSource ? params.referralSource : 'unknown'}`,
      level: 'info',
    });
  }

  //ADDED
  public sendSignupCompletedEvent(params: SignupCompletedParams): void {
    this.sendEvent('signup_completed', {
      event_category: 'authentication',
      ...params
    });
    addSystemLog({
      message: `${params.email} signed up with the method ${params.method}`,
      level: 'info',
    });
  }

  //ADDED
  public sendLogoutEvent(params: LogoutEventParams): void {
    this.sendEvent('logout', {
      event_category: 'authentication',
      ...params
    });
    addSystemLog({
      message: `${params.email} logged out`,
      level: 'info',
    });
  }

  //ADDED
  public sendOnboardingStartedEvent(params: OnboardingStartedParams): void {
    this.sendEvent('onboarding_started', {
      event_category: 'onboarding',
      ...params
    });
    addSystemLog({
      message: `Onboarding started for ${params.email}`,
      level: 'info',
    });
  }

  //ADDED
  public sendOnboardingStepViewedEvent(params: OnboardingStepViewedParams): void {
    this.sendEvent('onboarding_step_viewed', {
      event_category: 'onboarding',
      ...params
    });
    addSystemLog({
      message: `Onboarding step ${params.step} viewed for ${params.email}`,
      level: 'info',
    });
  }

  //ADDED
  public sendOnboardingStepCompletedEvent(params: OnboardingStepCompletedParams): void {
    this.sendEvent('onboarding_step_completed', {
      event_category: 'onboarding',
      ...params
    });
    addSystemLog({
      message: `Onboarding step ${params.step} completed for ${params.email}`,
      level: 'info',
    });
  }

  //ADDED
  public sendOnboardingFinishedEvent(params: OnboardingFinishedParams): void {
    this.sendEvent('onboarding_finished', {
      event_category: 'onboarding',
      ...params
    });
    addSystemLog({
      message: `Onboarding finished for ${params.email}`,
      level: 'info',
    });
  }

  //ADDED
  public sendPricingPageViewedEvent(params: PricingPageViewedParams): void {
    this.sendEvent('pricing_page_viewed', {
      event_category: 'pricing',
      ...params
    });
    addSystemLog({
      message: `Pricing page viewed by ${params.email}`,
      level: 'info',
    })
  }

  //ADDED
  public sendPricingPlanSelectedEvent(params: PricingPlanSelectedParams): void {
    this.sendEvent('pricing_plan_selected', {
      event_category: 'pricing',
      ...params
    });
    addSystemLog({
      message: `Pricing plan ${params.plan} selected by ${params.email}`,
      level: 'info',
    })
  }

  //ADDED
  public sendPlanSelectionSkippedEvent(params: PlanSelectionSkippedParams): void {
    this.sendEvent('plan_selection_skipped', {
      event_category: 'pricing',
      ...params
    });
    addSystemLog({
      message: `Pricing plan selection skipped by ${params.email}`,
      level: 'info',
    })
  }

  //ADDED
  public sendDashboardFirstTimeViewedEvent(params: DashboardFirstTimeViewedParams): void {
    this.sendEvent('dashboard_first_time_viewed', {
      event_category: 'dashboard',
      ...params
    });
    addSystemLog({
      message: `Dashboard viewed for the first time by ${params.email}`,
      level: 'info',
    })
  }

  //ADDED
  public sendAnalysisButtonClickedEvent(params: AnalysisButtonClickedParams): void {
    this.sendEvent('analysis_button_clicked', {
      event_category: 'dashboard',
      ...params
    });
    addSystemLog({
      message: `Analysis button clicked by ${params.email} from ${params.entryPoint}`,
      level: 'info',
    })
  }

  //ADDED
  public sendShopifyConnectionStartedEvent(params: ShopifyConnectionStartedParams): void {
    this.sendEvent('shopify_connection_started', {
      event_category: 'shopify',
      ...params
    });
    addSystemLog({
      message: `Shopify connection started by ${params.email}`,
      level: 'info',
    })
  }

  //ADDED
  public sendShopifyConnectionCompletedEvent(params: ShopifyConnectionCompletedParams): void {
    this.sendEvent('shopify_connection_completed', {
      event_category: 'shopify',
      ...params
    });
    addSystemLog({
      message: `Shopify connection completed by ${params.email}`,
      level: 'info',
    })
  }

  //ADDED
  public sendAnalysisStartedEvent(params: AnalysisStartedParams): void {
    this.sendEvent('analysis_started', {
      event_category: 'analysis',
      ...params
    });
    addSystemLog({
      message: `Analysis started by ${params.email}`,
      level: 'info',
    })
  }

  //ADDED
  public sendAnalysisInProgressEvent(params: AnalysisInProgressParams): void {
    this.sendEvent('analysis_in_progress', {
      event_category: 'analysis',
      ...params
    });
    addSystemLog({
      message: `Analysis in progress for ${params.email} - Progress: ${params.progress}%`,
      level: 'info',
    })
  }

  //ADDED
  public sendAnalysisCompletedEvent(params: AnalysisCompletedParams): void {
    this.sendEvent('analysis_completed', {
      event_category: 'analysis',
      ...params
    });
    addSystemLog({
      message: `Analysis completed by ${params.email}`,
      level: 'info',
    })
  }

  //ADDED
  public sendEnhancementMenuOpenedEvent(params: EnhancementMenuOpenedParams): void {
    this.sendEvent('enhancement_menu_opened', {
      event_category: 'enhancement',
      ...params
    });
    addSystemLog({
      message: `Enhancement menu opened by ${params.email}`,
      level: 'info',
    })
  }

  //ADDED
  public sendEnhancementJobStartedEvent(params: EnhancementJobStartedParams): void {
    this.sendEvent('enhancement_job_started', {
      event_category: 'enhancement',
      ...params
    });
    addSystemLog({
      message: `Enhancement job ${params.jobId} started by ${params.email} - Features: ${params.features.join(', ')}`,
      level: "info"
    })
  }

  //ADDED
  public sendEnhancementJobCompletedEvent(params: EnhancementJobCompletedParams): void {
    this.sendEvent('enhancement_job_completed', {
      event_category: 'enhancement',
      ...params
    });
    addSystemLog({
      message: `Enhancement job ${params.jobId} completed by ${params.email}`,
      level: "info"
    })
  }

  //ADDED
  public sendEnhancementJobResultsEditedEvent(params: EnhancementJobResultsEditedParams): void {
    this.sendEvent('enhancement_job_results_edited', {
      event_category: 'enhancement',
      ...params
    });
    addSystemLog({
      message: `Enhancement job ${params.jobId} results edited by ${params.email}`,
      level: "info"
    })
  }

  //ADDED
  public sendEnhancementJobExportedEvent(params: EnhancementJobExportedParams): void {
    this.sendEvent('enhancement_job_exported', {
      event_category: 'enhancement',
      ...params
    });
    addSystemLog({
      message: `Enhancement job ${params.jobId} exported to ${params.destination} by ${params.email}`,
      level: "info"
    })
  }

  //ADDED
  public sendSupportChatOpenedEvent(params: SupportChatOpenedParams): void {
    this.sendEvent('support_chat_opened', {
      event_category: 'support',
      ...params
    });
    addSystemLog({
      message: `Support chat opened by ${params.email}`,
      level: "info"
    })
  }

  
  public sendPlanUpgredeFlowStartedEvent(params: PlanUpgredeFlowStartedParams): void {
    this.sendEvent('plan_upgrade_flow_started', {
      event_category: 'pricing',
      ...params
    });
    addSystemLog({
      message: `Plan upgrade flow started by ${params.email}`,
      level: "info"
    })
  }

  public sendPlanUpgredeFlowCompletedEvent(params: PlanUpgredeFlowCompletedParams): void {
    this.sendEvent('plan_upgrade_flow_completed', {
      event_category: 'pricing',
      ...params
    });
    addSystemLog({
      message: `Plan upgrade flow completed by ${params.email}`,
      level: "info"
    })
  }


  public sendCustomEvent(
    action: string,
    params: GtagEventParams
  ): void {
    this.sendEvent(action, params);
  }
}

export const analytics = AnalyticsManager.getInstance();
analytics.init();